import {
  FhirQuestionnaireNames,
  GetFhirQuestionnaireByIdQuery,
  GetFhirQuestionnaireResponseQuery,
  useGetFhirQuestionnaireByIdQuery,
  useGetFhirQuestionnaireResponseQuery,
} from 'graphql/generated/remote-schema-hasura';
import {
  GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS,
  MEDICATIONS_QUESTION_ID,
  pageIds,
  questionnairesIds,
} from 'utilities/constants';
import { convertHealthQuestionnaireResponsesFromFhirToHasura } from 'utilities/functions';
import { useEffect, useState } from 'react';
import { useGetPage } from 'hooks/useGetPage';
import { useAuth } from 'auth/context/AuthContext';

export interface Response {
  id: string;
  value: string;
}

interface Answer {
  questionID: number;
  response: string | Response;
}

export interface IMedication {
  title: string;
  questionID: number;
  response: string | Response;
}

export interface QuestionnaireView {
  title: string;
  questionID: string;
  answer: unknown;
  dependentAnswers?: QuestionnaireView[];
  triggerParentValues?: string[];
}

const convertHealthQuestionnaireResponsesFromFhirToView = (
  responseData?: GetFhirQuestionnaireResponseQuery,
  questionData?: GetFhirQuestionnaireByIdQuery,
): QuestionnaireView[][] => {
  const rawResponses =
    responseData?.getFHIRQuestionnaireResponse.questionnaire_response.responses;
  const rawQuestions =
    questionData?.getFHIRQuestionnaireByCodexID
      .questionnaires_questionnaire_questions;

  if (!rawResponses?.length || !rawQuestions?.length) return [];

  const titleById = new Map(
    rawQuestions.map(({ questionnaire_questions_question }) => [
      questionnaire_questions_question.id.toString(),
      questionnaire_questions_question.text,
    ]),
  );

  const {
    questionnaire_responses: [{ responses }],
  } = convertHealthQuestionnaireResponsesFromFhirToHasura(
    responseData as GetFhirQuestionnaireResponseQuery,
  );

  const dataQuestionnaire = (responses as Array<Record<string, string>>).reduce<
    Record<string, QuestionnaireView[]>
  >((sections, { currentSection, questionID, response }) => {
    const title = titleById.get(questionID.toString()) ?? '';
    (sections[currentSection] ??= []).push({
      title,
      questionID: questionID,
      answer: response,
    });

    return sections;
  }, {});

  return Object.values(dataQuestionnaire);
};

const mergeMedicationResponses = (
  prev: string | Response,
  next: Response,
): string | Response =>
  typeof prev === 'string' ? next : `${prev.value} ${next.value}`;

export const convertMedications = (
  medications?: QuestionnaireView[],
  questionData?: Record<string, string>,
): IMedication[][] => {
  if (!medications?.length || !questionData) return [];

  return (medications[0]?.answer as { answers: Answer[] }[]).map(
    ({ answers }) => {
      const byQuestion = new Map<number, IMedication>();

      for (const { questionID, response } of answers) {
        const title = questionData[questionID.toString()] ?? '';
        const existing = byQuestion.get(questionID);

        byQuestion.set(questionID, {
          title,
          questionID,
          response: existing
            ? mergeMedicationResponses(existing.response, response as Response)
            : response,
        });
      }

      return [...byQuestion.values()];
    },
  );
};

export const useGetLastQuestionnaireByPatientCodexId = (
  codexPatientId: string,
) => {
  const { user } = useAuth();
  const [fhirQuestionnaireStoredData, setFhirQuestionnaireStoredData] =
    useState<QuestionnaireView[][]>([]);

  const { loading, data: locale } = useGetPage({
    pageId: pageIds.QUESTIONNAIRE_TAB_BY_MY_PATIENT_PAGE,
    locale: 'en',
  });

  const { loading: loadingAnswers, data: questionnaireAnswers } =
    useGetFhirQuestionnaireResponseQuery({
      context: {
        ignoreGlobalErrors: true,
      },
      skip: !user?.uuid,
      fetchPolicy: 'no-cache',
      variables: {
        codexPatientId: codexPatientId,
        identifier: FhirQuestionnaireNames.HealthQuestionnaire,
        system: GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS.codex,
      },
    });
  const { loading: loadingQuestions, data: questionnaireQuestions } =
    useGetFhirQuestionnaireByIdQuery({
      fetchPolicy: 'network-only',
      variables: {
        questionnaire_id: questionnairesIds.HEALTH_QUESTIONNAIRE,
      },
    });

  useEffect(() => {
    console.log(questionnaireAnswers, questionnaireQuestions);
    const [heals, info] = convertHealthQuestionnaireResponsesFromFhirToView(
      questionnaireAnswers,
      questionnaireQuestions,
    );

    setFhirQuestionnaireStoredData([info, heals]);
  }, [questionnaireAnswers, questionnaireQuestions, locale, codexPatientId]);

  return {
    loading: loadingAnswers || loadingQuestions || loading,
    data: fhirQuestionnaireStoredData,
    updatedAt:
      questionnaireAnswers?.getFHIRQuestionnaireResponse.questionnaire_response
        .updated_at,
  };
};

export const useGetLastChildQuestionnaireByPatientCodexId = (
  codexPatientId: string,
) => {
  const { user } = useAuth();
  const [fhirQuestionnaireStoredData, setFhirQuestionnaireStoredData] =
    useState<QuestionnaireView[][]>([]);

  const { loading, data: locale } = useGetPage({
    pageId: pageIds.QUESTIONNAIRE_TAB_BY_MY_PATIENT_PAGE,
    locale: 'en',
  });

  const { loading: loadingAnswers, data: questionnaireAnswers } =
    useGetFhirQuestionnaireResponseQuery({
      context: {
        ignoreGlobalErrors: true,
      },
      skip: !user?.uuid,
      fetchPolicy: 'no-cache',
      variables: {
        codexPatientId: codexPatientId,
        identifier: FhirQuestionnaireNames.HealthQuestionnaire,
        system: GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS.codex,
      },
    });
  const { loading: loadingQuestions, data: questionnaireQuestions } =
    useGetFhirQuestionnaireByIdQuery({
      fetchPolicy: 'network-only',
      variables: {
        questionnaire_id: questionnairesIds.CHILD_HEALTH_QUESTIONNAIRE,
      },
    });

  useEffect(() => {
    const [allergens, info] = convertHealthQuestionnaireResponsesFromFhirToView(
      questionnaireAnswers,
      questionnaireQuestions,
    );

    const filteredInfo = [...(info || []), ...(allergens || [])]?.filter(
      (item) => Number(item.questionID) !== MEDICATIONS_QUESTION_ID,
    );

    setFhirQuestionnaireStoredData([filteredInfo]);
  }, [questionnaireAnswers, questionnaireQuestions, locale]);

  return {
    loading: loadingAnswers || loadingQuestions || loading,
    data: fhirQuestionnaireStoredData,
    updatedAt:
      questionnaireAnswers?.getFHIRQuestionnaireResponse.questionnaire_response
        .updated_at,
  };
};
